import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Slider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import { VpnKey, FileCopy, Save, Refresh } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { generatorService } from "../services/generator.service";

const PasswordGenerator: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [length, setLength] = useState(12);
  const [lowercase, setLowercase] = useState(true);
  const [uppercase, setUppercase] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!lowercase && !uppercase && !numbers && !symbols) {
      alert("Selecione pelo menos um tipo de caractere");
      return;
    }

    setLoading(true);
    try {
      const { password: newPassword } = await generatorService.generate({
        length,
        lowercase,
        uppercase,
        numbers,
        symbols,
      });
      setPassword(newPassword);
    } catch (error: any) {
      alert(error.response?.data?.message || "Erro ao gerar senha");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (password) {
      await navigator.clipboard.writeText(password);
      alert("Senha copiada para a área de transferência!");
    }
  };

  const clearPassword = () => {
    setPassword("");
  };

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <VpnKey sx={{ fontSize: 40, color: "primary.main", mb: 2 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              Gerador de Senhas
            </Typography>

            <Box sx={{ width: "100%", mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Comprimento da Senha: {length}
              </Typography>
              <Slider
                value={length}
                onChange={(_, newValue) => setLength(newValue as number)}
                min={4}
                max={50}
                step={1}
                marks
                valueLabelDisplay="auto"
                sx={{ mb: 3 }}
              />

              <Typography variant="h6" gutterBottom>
                Incluir:
              </Typography>
              <FormGroup sx={{ mb: 3 }}>
                <FormControlLabel
                  control={<Checkbox checked={uppercase} onChange={(e) => setUppercase(e.target.checked)} />}
                  label="Letras Maiúsculas (A-Z)"
                />
                <FormControlLabel
                  control={<Checkbox checked={lowercase} onChange={(e) => setLowercase(e.target.checked)} />}
                  label="Letras Minúsculas (a-z)"
                />
                <FormControlLabel
                  control={<Checkbox checked={numbers} onChange={(e) => setNumbers(e.target.checked)} />}
                  label="Números (0-9)"
                />
                <FormControlLabel
                  control={<Checkbox checked={symbols} onChange={(e) => setSymbols(e.target.checked)} />}
                  label="Símbolos (!@#$%^&*)"
                />
              </FormGroup>

              <Button
                fullWidth
                variant="contained"
                onClick={handleGenerate}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <VpnKey />}
                sx={{ mb: 3 }}
              >
                {loading ? "Gerando..." : "Gerar Nova Senha"}
              </Button>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Senha:
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Digite ou gere uma senha..."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  helperText="Você pode editar a senha gerada ou digitar sua própria senha"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {password && (
                          <>
                            <IconButton aria-label="copiar senha" onClick={handleCopy} edge="end" color="primary" title="Copiar senha">
                              <FileCopy />
                            </IconButton>
                            <IconButton aria-label="limpar senha" onClick={clearPassword} edge="end" color="secondary" title="Limpar senha">
                              <Refresh />
                            </IconButton>
                          </>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {/* Análise de força da senha */}
                {password && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Comprimento: {password.length} caracteres
                    </Typography>
                    <Typography
                      variant="body2"
                      color={password.length >= 12 ? "success.main" : password.length >= 8 ? "warning.main" : "error.main"}
                    >
                      Força: {password.length >= 12 ? "Forte" : password.length >= 8 ? "Média" : "Fraca"}
                    </Typography>
                  </Box>
                )}
              </Box>

              {isAuthenticated && password && (
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  component={Link}
                  to="/save-password"
                  state={{ generatedPassword: password }}
                  startIcon={<Save />}
                  sx={{ mb: 2 }}
                >
                  Salvar Esta Senha
                </Button>
              )}

              {!isAuthenticated && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Link to="/login" style={{ color: "inherit", fontWeight: "bold" }}>
                    Faça login
                  </Link>{" "}
                  para salvar suas senhas geradas
                </Alert>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default PasswordGenerator;
